import React, { useState, useEffect, useCallback } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { library } from '@fortawesome/fontawesome-svg-core'
import { faAdjust, faSun, faMoon } from '@fortawesome/free-solid-svg-icons'
import PropTypes from 'prop-types'
import logo from '../../assets/veta-icon.svg'
import vetaLogoWhite from '../../assets/veta-logo-white.svg'
import { useTheme } from '../../contexts/ThemeContext'
import { isDevelopment } from '../../devAuth'
import usePendingSetupCount from '../../hooks/usePendingSetupCount'
import useBillingDraftCount from '../../hooks/useBillingDraftCount'
import useAuthz from '../../hooks/useAuthz'
import { hasVisibleSettingsItems } from '../../views/settings/settingsMenuItems'
import styles from './Sidebar.module.scss'

library.add(faAdjust, faSun, faMoon)

const COLLAPSED_KEY = 'sidebar_collapsed'

const THEME_ICONS = { auto: 'adjust', light: 'sun', dark: 'moon' }
const THEME_LABELS = { auto: 'Auto', light: 'Light', dark: 'Dark' }

const getInitialsFallback = (name) => {
  const initials = (name || 'U')
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase()
  return (
    'data:image/svg+xml,' +
    encodeURIComponent(
      `<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100" viewBox="0 0 100 100"><rect width="100" height="100" rx="50" fill="#1e3a5f"/><text x="50" y="58" text-anchor="middle" fill="#fff" font-family="system-ui" font-size="40" font-weight="600">${initials}</text></svg>`
    )
  )
}
const handleAvatarError = (e, name) => {
  e.target.src = getInitialsFallback(name)
}

const Sidebar = ({ isAuthenticated, user, onLogout }) => {
  const { isAdmin, organization } = useAuthz()
  const location = useLocation()
  const { theme, cycleTheme } = useTheme()
  const pendingSetup = usePendingSetupCount(isAdmin)
  const draftInvoiceCount = useBillingDraftCount(isAdmin)

  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(COLLAPSED_KEY) === 'true'
    } catch {
      return false
    }
  })
  const [mobileOpen, setMobileOpen] = useState(false)

  useEffect(() => {
    try {
      // Same-value guard skips initial mount and prevents cross-tab echo loops.
      if ((localStorage.getItem(COLLAPSED_KEY) === 'true') === collapsed) return
      localStorage.setItem(COLLAPSED_KEY, String(collapsed))
    } catch {
      return
    }
    window.dispatchEvent(new Event('sidebar-collapsed-change'))
  }, [collapsed])

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key !== null && e.key !== COLLAPSED_KEY) return
      try {
        setCollapsed(localStorage.getItem(COLLAPSED_KEY) === 'true')
      } catch {
        /* noop */
      }
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [])

  const toggleCollapsed = useCallback(() => {
    setCollapsed((prev) => !prev)
  }, [])

  const closeMobile = useCallback(() => {
    setMobileOpen(false)
  }, [])

  /* eslint-disable react-hooks/set-state-in-effect -- close mobile nav on route change */
  useEffect(() => {
    setMobileOpen(false)
  }, [location.pathname])
  /* eslint-enable react-hooks/set-state-in-effect */

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setMobileOpen(false)
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  if (!isAuthenticated) return null

  const getPath = (basePath) => {
    const urlParams = new URLSearchParams(location.search)
    const startDate = urlParams.get('startDate')
    const endDate = urlParams.get('endDate')
    if (startDate || endDate) {
      const dateParams = new URLSearchParams()
      if (startDate) dateParams.set('startDate', startDate)
      if (endDate) dateParams.set('endDate', endDate)
      return `${basePath}?${dateParams.toString()}`
    }
    return basePath
  }

  const mainLinks = [
    { to: getPath('/'), icon: 'chart-pie', label: 'Impact Overview', exact: true },
    { to: getPath('/dashboard'), icon: 'tachometer-alt', label: 'Dashboard' }
  ]

  const adminLinks = [
    { to: getPath('/campaigns'), icon: 'building', label: 'Campaigns', exact: true },
    { to: '/campaigns/manage', icon: 'cog', label: 'Campaign Mgmt', exact: true },
    { to: getPath('/calls'), icon: 'phone', label: 'Call Search', exact: true },
    { to: getPath('/qa-summary'), icon: 'clipboard-check', label: 'QA Summary', exact: true },
    { to: getPath('/usage-overview'), icon: 'chart-bar', label: 'Usage Overview', exact: true },
    {
      to: '/billing/admin',
      icon: 'file-invoice-dollar',
      label: 'Billing Management',
      exact: true,
      badge: (pendingSetup.count || 0) + (draftInvoiceCount || 0)
    },
    { to: '/billing/admin/logs', icon: 'list-alt', label: 'Logs', exact: true },
    { to: '/auth-migration', icon: 'shield-alt', label: 'Auth Migration', exact: true },
    ...(isDevelopment
      ? [{ to: '/availability', icon: 'users', label: 'Availability', exact: true, devOnly: true }]
      : [])
  ]

  const five9Links = [{ to: '/five9-sync', icon: 'sync', label: 'Five9 Data Sync', exact: true }]

  const toolLinks = [
    ...(isDevelopment
      ? [
          {
            to: `/sop/${encodeURIComponent(organization)}`,
            icon: 'file-alt',
            label: 'Call SOP (dev mode)',
            exact: true
          }
        ]
      : []),
    { to: '/glossary', icon: 'book', label: 'Glossary', exact: true },
    ...(isDevelopment
      ? [{ to: '/billing', icon: 'credit-card', label: 'Billing', exact: true, devOnly: true }]
      : []),
    ...(hasVisibleSettingsItems(isAdmin)
      ? [{ to: '/settings', icon: 'cog', label: 'Settings', exact: false }]
      : [])
  ]

  const sidebarClass = [
    styles.sidebar,
    collapsed ? styles.collapsed : '',
    mobileOpen ? styles.mobileOpen : ''
  ]
    .filter(Boolean)
    .join(' ')

  const renderLink = (link) => (
    <NavLink
      key={link.to}
      to={link.to}
      end={link.exact}
      className={({ isActive }) =>
        [styles.navItem, isActive ? styles.active : ''].filter(Boolean).join(' ')
      }
      onClick={closeMobile}
    >
      <FontAwesomeIcon icon={link.icon} className={styles.navIcon} fixedWidth />
      <span className={styles.navLabel}>{link.label}</span>
      {link.devOnly && <span className={styles.devOnlyTag}>Dev only</span>}
      {link.badge > 0 && <span className={styles.navBadge}>{link.badge}</span>}
    </NavLink>
  )

  return (
    <>
      {/* Mobile top bar */}
      <div className={styles.mobileTopBar}>
        <button
          className={styles.mobileHamburger}
          onClick={() => setMobileOpen(true)}
          aria-label='Open navigation'
        >
          <FontAwesomeIcon icon='bars' />
        </button>
        <img src={logo} alt='Veta Virtual' className={styles.mobileLogo} />
        {user?.picture ? (
          <img
            src={user.picture}
            alt={`${user?.name || 'User'} avatar`}
            className={styles.mobileAvatar}
            onError={(e) => handleAvatarError(e, user?.name)}
          />
        ) : (
          <div style={{ width: 28 }} />
        )}
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className={`${styles.overlay} ${mobileOpen ? styles.overlayVisible : ''}`}
          onClick={closeMobile}
        />
      )}

      {/* Sidebar */}
      <nav
        className={sidebarClass}
        aria-label='Main navigation'
        style={isDevelopment ? { paddingBottom: 32 } : undefined}
      >
        {/* Brand */}
        <div className={styles.brandSection}>
          <img src={vetaLogoWhite} alt='Veta Virtual' className={styles.brandLogoFull} />
          <img src={logo} alt='V' className={styles.brandLogoCollapsed} />
        </div>

        {/* Navigation links */}
        <div className={styles.navSection}>
          <div className={styles.sectionLabel}>Main</div>
          {mainLinks.map(renderLink)}

          {isAdmin && (
            <>
              <div className={styles.sectionLabel}>Admin</div>
              {adminLinks.map(renderLink)}
              {five9Links.map(renderLink)}
            </>
          )}

          <div className={styles.sectionLabel}>Tools</div>
          {toolLinks.map(renderLink)}
        </div>

        {/* Bottom section */}
        <div className={styles.bottomSection}>
          {/* Theme toggle */}
          <button
            className={styles.themeToggle}
            onClick={cycleTheme}
            title={`Theme: ${THEME_LABELS[theme]}`}
            aria-label={`Switch theme, current: ${THEME_LABELS[theme]}`}
          >
            <FontAwesomeIcon icon={THEME_ICONS[theme]} className={styles.themeIcon} fixedWidth />
            <span className={styles.themeLabel}>{THEME_LABELS[theme]}</span>
          </button>

          {/* User info */}
          <div className={styles.userSection}>
            {user?.picture && (
              <img
                src={user.picture}
                alt={`${user?.name || 'User'} avatar`}
                className={styles.userAvatar}
                onError={(e) => handleAvatarError(e, user?.name)}
              />
            )}
            <div className={styles.userInfo}>
              <div className={styles.userName}>{user?.name || 'User'}</div>
              <div className={styles.userRole}>{isAdmin ? 'Administrator' : 'User'}</div>
            </div>
            <button
              className={styles.logoutButton}
              onClick={onLogout}
              title='Log out'
              aria-label='Log out'
            >
              <FontAwesomeIcon icon='sign-out-alt' />
            </button>
          </div>

          {/* Collapse toggle */}
          <button
            className={styles.collapseButton}
            onClick={toggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <FontAwesomeIcon icon='chevron-left' className={styles.collapseIcon} fixedWidth />
            {!collapsed && <span className={styles.collapseLabel}>Collapse</span>}
          </button>
        </div>
      </nav>
    </>
  )
}

Sidebar.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  user: PropTypes.object,
  onLogout: PropTypes.func.isRequired
}

export default Sidebar
